import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Edit3,
  Trash2,
  X,
  Save,
  ChevronDown,
  ChevronUp,
  Users,
  Boxes,
  Clock,
  ClipboardList,
  Loader2,
  BadgeCheck,
  Zap,
  BarChart3,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { apiGet, apiPost, apiPut, apiDelete } from '@/services/api';
import { API_ROUTES, Role, type ResourceType, type FlightPhase } from '@avgrp/shared';
import { useAuth } from '@/hooks/useAuth';
import { hasMinRole } from '@/utils/roles';

// ─── Types ──────────────────────────────────────────────────

interface AircraftTypeRef {
  id: string;
  icaoCode: string;
  name: string;
  category: string;
}

interface ResourceOverrideRef {
  id: string;
  aircraftTypeId: string;
  quantity: number;
  aircraftType: AircraftTypeRef;
}

interface ResourceRequirement {
  id: string;
  resourceType: ResourceType;
  qualificationId: string | null;
  materialTypeId: string | null;
  quantity: number;
  costPerHour: string; // Decimal serialized as string
  isOptional: boolean;
  qualification: { id: string; name: string; code: string } | null;
  materialType: { id: string; name: string; code: string } | null;
  overrides: ResourceOverrideRef[];
}

interface PhaseScheduleRef {
  id: string;
  phase: FlightPhase;
  startTrigger: string;
  predecessorId: string | null;
  offsetMinutes: number;
  predecessor: { id: string; name: string; code: string } | null;
}

interface PhaseScheduleFormItem {
  phase: FlightPhase;
  startTrigger: string;
  predecessorId: string | null;
  offsetMinutes: number;
}

interface TaskTemplate {
  id: string;
  name: string;
  code: string;
  description: string | null;
  defaultDuration: number;
  estimatedCost: string | null; // Decimal serialized as string (auto-computed)
  isRoutine: boolean;
  priority: number;
  isActive: boolean;
  durationOverrides: DurationOverride[];
  aircraftTypes: AircraftTypeRef[];
  resourceRequirements: ResourceRequirement[];
  phaseSchedules: PhaseScheduleRef[];
}

interface DurationOverride {
  id: string;
  taskTemplateId: string;
  aircraftTypeId: string;
  duration: number;
  aircraftType: AircraftTypeRef;
}

interface DurationOverrideFormItem {
  aircraftTypeId: string;
  duration: number;
}

interface AircraftType {
  id: string;
  icaoCode: string;
  iataCode: string | null;
  name: string;
  manufacturer: string | null;
  category: string;
}

interface Qualification {
  id: string;
  name: string;
  code: string;
}

interface MaterialType {
  id: string;
  name: string;
  code: string;
}

interface ResourceOverrideFormItem {
  aircraftTypeId: string;
  quantity: number;
}

interface ResourceRequirementFormItem {
  resourceType: 'HUMAN' | 'MATERIAL';
  qualificationId?: string;
  materialTypeId?: string;
  quantity: number;
  costPerHour: number;
  isOptional: boolean;
  overrides: ResourceOverrideFormItem[];
}

interface FormErrors {
  name?: string;
  code?: string;
  defaultDuration?: string;
  aircraftTypeIds?: string;
  phaseSchedules?: string;
  general?: string;
}

// ─── Phase / category label helpers ─────────────────────────

const phaseColors: Record<string, string> = {
  PREPARATION: 'bg-blue-50 text-blue-700',
  TURNAROUND: 'bg-amber-50 text-amber-700',
  ARRIVAL: 'bg-emerald-50 text-emerald-700',
};

const categoryColors: Record<string, string> = {
  REGIONAL: 'bg-teal-50 text-teal-700',
  MEDIUM: 'bg-indigo-50 text-indigo-700',
  HEAVY: 'bg-orange-50 text-orange-700',
};

// ─── Inline error label ────────────────────────────────────

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1 flex items-center gap-1 text-xs text-red-500">
      <AlertCircle className="h-3 w-3 flex-shrink-0" />
      {message}
    </p>
  );
}

// ─── Component ──────────────────────────────────────────────

export function TasksPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Form state matching the API create schema
  const emptyForm = {
    name: '',
    code: '',
    description: '',
    defaultDuration: 15,
    isRoutine: true,
    priority: 0,
    aircraftTypeIds: [] as string[],
    resourceRequirements: [] as ResourceRequirementFormItem[],
    isActive: true,
    durationOverrides: [] as DurationOverrideFormItem[],
    phaseSchedules: [] as PhaseScheduleFormItem[],
  };
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  // State for the inline "add resource requirement" row
  const [newReq, setNewReq] = useState<ResourceRequirementFormItem>({
    resourceType: 'HUMAN',
    qualificationId: undefined,
    materialTypeId: undefined,
    quantity: 1,
    costPerHour: 0,
    isOptional: false,
    overrides: [],
  });

  // Track which resource requirement indices have expanded override sections
  const [expandedReqIdx, setExpandedReqIdx] = useState<number | null>(null);

  // ─── Data queries ────────────────────────────────────────

  const { data: templates, isLoading } = useQuery({
    queryKey: ['tasks', 'templates'],
    queryFn: () => apiGet<{ data: TaskTemplate[] }>(`${API_ROUTES.TASKS}/templates`),
    select: (res) => res.data,
  });

  const {
    data: aircraftTypesData,
    isLoading: isAircraftTypesLoading,
    isError: isAircraftTypesError,
    refetch: refetchAircraftTypes,
  } = useQuery({
    queryKey: ['aircraft-types'],
    queryFn: () => apiGet<{ data: AircraftType[] }>(`${API_ROUTES.RESOURCES}/aircraft-types`),
    select: (res) => res.data,
    retry: 2,
  });

  const { data: qualificationsData } = useQuery({
    queryKey: ['qualifications'],
    queryFn: () => apiGet<{ data: Qualification[] }>(`${API_ROUTES.RESOURCES}/qualifications`),
    select: (res) => res.data,
  });

  const { data: materialTypesData } = useQuery({
    queryKey: ['material-types'],
    queryFn: () => apiGet<{ data: MaterialType[] }>(`${API_ROUTES.RESOURCES}/material-types`),
    select: (res) => res.data,
  });

  const aircraftTypes = aircraftTypesData ?? [];
  const qualifications = qualificationsData ?? [];
  const materialTypes = materialTypesData ?? [];

  // ─── Mutations ───────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      apiPost(`${API_ROUTES.TASKS}/templates`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'templates'] });
      setShowCreateForm(false);
      resetForm();
    },
    onError: (err: Error) => {
      setFormErrors((prev) => ({
        ...prev,
        general: err.message || 'Failed to create template. Please check your inputs.',
      }));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof formData }) =>
      apiPut(`${API_ROUTES.TASKS}/templates/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'templates'] });
      setEditingId(null);
      resetForm();
    },
    onError: (err: Error) => {
      setFormErrors((prev) => ({
        ...prev,
        general: err.message || 'Failed to update template. Please check your inputs.',
      }));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`${API_ROUTES.TASKS}/templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'templates'] });
      setDeleteConfirmId(null);
    },
  });

  // ─── Derived data ───────────────────────────────────────

  const templateList = templates ?? [];

  const filteredTemplates = templateList.filter((t) => {
    const matchesSearch =
      !searchQuery ||
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.description ?? '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPhase = !phaseFilter || t.phaseSchedules.some((ps) => ps.phase === phaseFilter);
    return matchesSearch && matchesPhase;
  });

  // ─── Form validation ──────────────────────────────────

  const validateForm = useCallback((): boolean => {
    const errors: FormErrors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!formData.code.trim()) {
      errors.code = 'Code is required';
    } else if (!/^[A-Z0-9_-]+$/.test(formData.code)) {
      errors.code = 'Code must be uppercase letters, numbers, hyphens, or underscores';
    }
    if (formData.defaultDuration < 1) {
      errors.defaultDuration = 'Duration must be at least 1 minute';
    }
    if (formData.aircraftTypeIds.length === 0) {
      errors.aircraftTypeIds = 'Select at least one aircraft type';
    }
    if (formData.phaseSchedules.length === 0) {
      errors.phaseSchedules = 'At least one phase schedule is required';
    }
    for (const ps of formData.phaseSchedules) {
      if (ps.startTrigger === 'AFTER_TASK' && !ps.predecessorId) {
        errors.phaseSchedules = `Predecessor is required for "${ps.phase}" phase when using "After another task" trigger`;
        break;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  // ─── Form helpers ──────────────────────────────────────

  function resetForm() {
    setFormData({ ...emptyForm, aircraftTypeIds: [], resourceRequirements: [], phaseSchedules: [] });
    setFormErrors({});
    setNewReq({ resourceType: 'HUMAN', qualificationId: undefined, materialTypeId: undefined, quantity: 1, costPerHour: 0, isOptional: false, overrides: [] });
    setExpandedReqIdx(null);
  }

  function startEdit(template: TaskTemplate) {
    setEditingId(template.id);
    setShowCreateForm(false);
    setFormErrors({});
    setExpandedReqIdx(null);
    setFormData({
      name: template.name,
      code: template.code,
      description: template.description ?? '',
      defaultDuration: template.defaultDuration,
      isRoutine: template.isRoutine,
      priority: template.priority,
      aircraftTypeIds: template.aircraftTypes.map((at) => at.id),
      resourceRequirements: template.resourceRequirements.map((rr) => ({
        resourceType: rr.resourceType as 'HUMAN' | 'MATERIAL',
        qualificationId: rr.qualificationId ?? undefined,
        materialTypeId: rr.materialTypeId ?? undefined,
        quantity: rr.quantity,
        costPerHour: Number(rr.costPerHour),
        isOptional: rr.isOptional,
        overrides: rr.overrides.map((o) => ({
          aircraftTypeId: o.aircraftTypeId,
          quantity: o.quantity,
        })),
      })),
      isActive: template.isActive,
      durationOverrides: template.durationOverrides.map((ov) => ({
        aircraftTypeId: ov.aircraftTypeId,
        duration: ov.duration,
      })),
      phaseSchedules: template.phaseSchedules.map((ps) => ({
        phase: ps.phase,
        startTrigger: ps.startTrigger,
        predecessorId: ps.predecessorId,
        offsetMinutes: ps.offsetMinutes,
      })),
    });
  }

  function handleSave() {
    if (!validateForm()) return;

    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  }

  function toggleAircraftType(id: string) {
    setFormData((prev) => ({
      ...prev,
      aircraftTypeIds: prev.aircraftTypeIds.includes(id)
        ? prev.aircraftTypeIds.filter((atId) => atId !== id)
        : [...prev.aircraftTypeIds, id],
    }));
    // Clear aircraft type error when user makes a selection
    if (formErrors.aircraftTypeIds) {
      setFormErrors((prev) => ({ ...prev, aircraftTypeIds: undefined }));
    }
  }

  function addResourceRequirement() {
    const hasRef =
      (newReq.resourceType === 'HUMAN' && newReq.qualificationId) ||
      (newReq.resourceType === 'MATERIAL' && newReq.materialTypeId);
    if (!hasRef) return;

    setFormData((prev) => ({
      ...prev,
      resourceRequirements: [...prev.resourceRequirements, { ...newReq }],
    }));
    setNewReq({ resourceType: 'HUMAN', qualificationId: undefined, materialTypeId: undefined, quantity: 1, costPerHour: 0, isOptional: false, overrides: [] });
  }

  function removeResourceRequirement(index: number) {
    setFormData((prev) => ({
      ...prev,
      resourceRequirements: prev.resourceRequirements.filter((_, i) => i !== index),
    }));
  }

  /** Look up a qualification name by id */
  function getQualName(id?: string) {
    return qualifications.find((q) => q.id === id)?.name ?? 'Unknown';
  }
  /** Look up a material type name by id */
  function getMatTypeName(id?: string) {
    return materialTypes.find((m) => m.id === id)?.name ?? 'Unknown';
  }

  /** Clear a specific field error on change */
  function clearError(field: keyof FormErrors) {
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  function closeForm() {
    setShowCreateForm(false);
    setEditingId(null);
    resetForm();
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Task Templates</h1>
          <p className="mt-1 text-sm text-gray-500">
            Define task templates and their resource requirements
          </p>
        </div>
        {hasMinRole(user?.role, Role.MANAGER) && (
          <button
            onClick={() => {
              resetForm();
              setShowCreateForm(true);
              setEditingId(null);
            }}
            className="btn-primary"
          >
            <Plus className="mr-2 h-4 w-4" />
            New Template
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates by name, code, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field pl-9"
          />
        </div>
        <select
          value={phaseFilter}
          onChange={(e) => setPhaseFilter(e.target.value)}
          className="input-field w-auto"
        >
          <option value="">All phases</option>
          <option value="PREPARATION">Preparation</option>
          <option value="TURNAROUND">Turnaround</option>
          <option value="ARRIVAL">Arrival</option>
        </select>
      </div>

      {/* ── Create/Edit form ── */}
      {hasMinRole(user?.role, Role.MANAGER) && (showCreateForm || editingId) && (
        <div className="card border-aviation-200 bg-aviation-50/30">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingId ? 'Edit Template' : 'New Template'}
            </h3>
            <button onClick={closeForm} className="rounded-lg p-1 text-gray-400 hover:bg-gray-200">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* General error */}
          {formErrors.general && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {formErrors.general}
            </div>
          )}

          {/* Scrollable form body */}
          <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
            {/* Row 1: Name, Code, Duration, Priority */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData((p) => ({ ...p, name: e.target.value }));
                    clearError('name');
                  }}
                  className={`input-field ${formErrors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''}`}
                  placeholder="e.g., Passenger Boarding"
                />
                <FieldError message={formErrors.name} />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => {
                    setFormData((p) => ({ ...p, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '') }));
                    clearError('code');
                  }}
                  className={`input-field font-mono ${formErrors.code ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''}`}
                  placeholder="e.g., PAX-BOARD"
                />
                <FieldError message={formErrors.code} />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Duration (min) *</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={formData.defaultDuration}
                  onChange={(e) => {
                    const val = Math.max(1, Math.round(Number(e.target.value)));
                    setFormData((p) => ({ ...p, defaultDuration: val }));
                    clearError('defaultDuration');
                  }}
                  className={`input-field ${formErrors.defaultDuration ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''}`}
                />
                <FieldError message={formErrors.defaultDuration} />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Priority <span className="font-normal text-gray-400">(0-100)</span></label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={formData.priority}
                  onChange={(e) => {
                    const val = Math.min(100, Math.max(0, Math.round(Number(e.target.value))));
                    setFormData((p) => ({ ...p, priority: val }));
                  }}
                  className="input-field"
                />
              </div>
            </div>

            {/* Aircraft Types */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Aircraft Types *</label>
              {isAircraftTypesLoading ? (
                <div className="flex items-center gap-2 py-3 text-sm text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading aircraft types...
                </div>
              ) : isAircraftTypesError ? (
                <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>Failed to load aircraft types.</span>
                  <button type="button" onClick={() => refetchAircraftTypes()} className="ml-auto flex items-center gap-1 rounded-md border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50">
                    <RefreshCw className="h-3 w-3" /> Retry
                  </button>
                </div>
              ) : aircraftTypes.length === 0 ? (
                <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  No aircraft types configured.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {aircraftTypes.map((at) => (
                    <button
                      key={at.id}
                      type="button"
                      onClick={() => toggleAircraftType(at.id)}
                      className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${formData.aircraftTypeIds.includes(at.id) ? 'border-aviation-500 bg-aviation-50 text-aviation-700' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
                    >
                      {formData.aircraftTypeIds.includes(at.id) && <BadgeCheck className="mr-1 inline h-3.5 w-3.5" />}
                      {at.icaoCode} – {at.name}
                      <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] ${categoryColors[at.category] ?? 'bg-gray-100 text-gray-600'}`}>{at.category}</span>
                    </button>
                  ))}
                </div>
              )}
              <FieldError message={formErrors.aircraftTypeIds} />
            </div>

            {/* Duration by Aircraft Type */}
            {formData.aircraftTypeIds.length > 0 && (
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Duration by Aircraft Type
                  <span className="ml-1 font-normal text-gray-400">(override default per type)</span>
                </label>
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Aircraft Type</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Duration (min)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {formData.aircraftTypeIds.map((atId) => {
                        const at = aircraftTypes.find((a) => a.id === atId);
                        if (!at) return null;
                        const override = formData.durationOverrides.find((o) => o.aircraftTypeId === atId);
                        const currentDuration = override?.duration ?? formData.defaultDuration;
                        const isOverridden = override !== undefined;
                        return (
                          <tr key={atId} className={isOverridden ? 'bg-aviation-50/30' : ''}>
                            <td className="px-3 py-2">
                              <span className="font-medium text-gray-900">{at.icaoCode} – {at.name}</span>
                              <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] ${categoryColors[at.category] ?? 'bg-gray-100 text-gray-600'}`}>
                                {at.category}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min={1}
                                  step={1}
                                  value={currentDuration}
                                  onChange={(e) => {
                                    const val = Math.max(1, Math.round(Number(e.target.value)));
                                    setFormData((prev) => {
                                      const existing = prev.durationOverrides.filter((o) => o.aircraftTypeId !== atId);
                                      if (val !== prev.defaultDuration) {
                                        existing.push({ aircraftTypeId: atId, duration: val });
                                      }
                                      return { ...prev, durationOverrides: existing };
                                    });
                                  }}
                                  className="input-field w-20 text-sm"
                                />
                                {isOverridden && (
                                  <span className="text-[10px] font-medium text-aviation-600">overridden</span>
                                )}
                                {!isOverridden && (
                                  <span className="text-[10px] text-gray-400">default</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Phase Schedules */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Phase Scheduling *
                <span className="ml-1 font-normal text-gray-400">(select one or more phases)</span>
              </label>
              <div className="space-y-2">
                {(['PREPARATION', 'TURNAROUND', 'ARRIVAL'] as FlightPhase[]).map((phase) => {
                  const schedule = formData.phaseSchedules.find((ps) => ps.phase === phase);
                  const isEnabled = !!schedule;
                  return (
                    <div key={phase} className={`rounded-lg border ${isEnabled ? 'border-aviation-300 bg-aviation-50/30' : 'border-gray-200 bg-white'}`}>
                      <div className="flex items-center gap-3 px-3 py-2">
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={(e) => {
                            setFormData((prev) => {
                              if (e.target.checked) {
                                return {
                                  ...prev,
                                  phaseSchedules: [...prev.phaseSchedules, { phase, startTrigger: 'PHASE_START', predecessorId: null, offsetMinutes: 0 }],
                                };
                              }
                              return {
                                ...prev,
                                phaseSchedules: prev.phaseSchedules.filter((ps) => ps.phase !== phase),
                              };
                            });
                            if (formErrors.phaseSchedules) setFormErrors((prev) => ({ ...prev, phaseSchedules: undefined }));
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-aviation-600 focus:ring-aviation-500"
                        />
                        <span className={`badge text-xs ${phaseColors[phase] ?? 'bg-gray-100 text-gray-600'}`}>{phase}</span>
                      </div>
                      {isEnabled && (
                        <div className="border-t border-gray-100 px-3 py-2">
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                            <div>
                              <label className="mb-1 block text-xs text-gray-500">Start Trigger</label>
                              <select
                                value={schedule.startTrigger}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setFormData((prev) => ({
                                    ...prev,
                                    phaseSchedules: prev.phaseSchedules.map((ps) =>
                                      ps.phase === phase
                                        ? { ...ps, startTrigger: val, predecessorId: val !== 'AFTER_TASK' ? null : ps.predecessorId, offsetMinutes: val !== 'AFTER_TASK' ? 0 : ps.offsetMinutes }
                                        : ps
                                    ),
                                  }));
                                }}
                                className="input-field text-sm"
                              >
                                <option value="PHASE_START">At phase start</option>
                                <option value="PHASE_END">At phase end</option>
                                <option value="AFTER_TASK">After another task</option>
                              </select>
                            </div>
                            {schedule.startTrigger === 'AFTER_TASK' && (
                              <>
                                <div>
                                  <label className="mb-1 block text-xs text-gray-500">Predecessor Task</label>
                                  <select
                                    value={schedule.predecessorId ?? ''}
                                    onChange={(e) => {
                                      setFormData((prev) => ({
                                        ...prev,
                                        phaseSchedules: prev.phaseSchedules.map((ps) =>
                                          ps.phase === phase ? { ...ps, predecessorId: e.target.value || null } : ps
                                        ),
                                      }));
                                    }}
                                    className="input-field text-sm"
                                  >
                                    <option value="">Select predecessor...</option>
                                    {templateList.filter((t) => t.id !== editingId && t.isActive).map((t) => (
                                      <option key={t.id} value={t.id}>
                                        {t.name} ({t.code})
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="mb-1 block text-xs text-gray-500">Offset (min)</label>
                                  <input
                                    type="number"
                                    min={-60}
                                    max={120}
                                    value={schedule.offsetMinutes}
                                    onChange={(e) => {
                                      const val = Math.min(120, Math.max(-60, Math.round(Number(e.target.value))));
                                      setFormData((prev) => ({
                                        ...prev,
                                        phaseSchedules: prev.phaseSchedules.map((ps) =>
                                          ps.phase === phase ? { ...ps, offsetMinutes: val } : ps
                                        ),
                                      }));
                                    }}
                                    className="input-field text-sm"
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <FieldError message={formErrors.phaseSchedules} />
            </div>

            {/* Resource Requirements */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Resource Requirements</label>

              {/* Existing requirements list */}
              {formData.resourceRequirements.length > 0 && (
                <div className="mb-3 space-y-2">
                  {formData.resourceRequirements.map((req, idx) => (
                    <div
                      key={`req-${idx}-${req.resourceType}-${req.qualificationId ?? req.materialTypeId ?? 'none'}`}
                      className="rounded-lg border border-gray-200 bg-white"
                    >
                      <div className="flex items-center gap-3 px-3 py-2">
                        <span className={`badge text-xs ${req.resourceType === 'HUMAN' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>
                          {req.resourceType === 'HUMAN' ? (
                            <><Users className="mr-1 inline h-3 w-3" />Human</>
                          ) : (
                            <><Boxes className="mr-1 inline h-3 w-3" />Material</>
                          )}
                        </span>
                        <span className="flex-1 text-sm font-medium text-gray-900">
                          {req.resourceType === 'HUMAN'
                            ? getQualName(req.qualificationId)
                            : getMatTypeName(req.materialTypeId)}
                        </span>
                        <span className="text-sm text-gray-500">×{req.quantity}</span>
                        {req.isOptional && (
                          <span className="badge bg-gray-100 text-xs text-gray-500">Optional</span>
                        )}
                        {formData.aircraftTypeIds.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setExpandedReqIdx(expandedReqIdx === idx ? null : idx)}
                            className="rounded p-1 text-gray-400 hover:bg-gray-100"
                            title="Per-aircraft-type quantity overrides"
                          >
                            {expandedReqIdx === idx ? (
                              <ChevronUp className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronDown className="h-3.5 w-3.5" />
                            )}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            removeResourceRequirement(idx);
                            if (expandedReqIdx === idx) setExpandedReqIdx(null);
                            else if (expandedReqIdx !== null && expandedReqIdx > idx) setExpandedReqIdx(expandedReqIdx - 1);
                          }}
                          className="rounded-lg p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      {/* Per-aircraft-type quantity overrides */}
                      {expandedReqIdx === idx && formData.aircraftTypeIds.length > 0 && (
                        <div className="border-t border-gray-100 bg-gray-50/50 px-3 py-2">
                          <p className="mb-1.5 text-xs font-medium text-gray-500">Qty by aircraft type</p>
                          <div className="space-y-1.5">
                            {formData.aircraftTypeIds.map((atId) => {
                              const at = aircraftTypes.find((a) => a.id === atId);
                              if (!at) return null;
                              const ov = req.overrides.find((o) => o.aircraftTypeId === atId);
                              return (
                                <div key={atId} className="flex items-center gap-2">
                                  <span className="w-40 text-xs text-gray-600">
                                    {at.icaoCode} – {at.name}
                                    <span className={`ml-1 rounded-full px-1 py-0.5 text-[9px] ${categoryColors[at.category] ?? 'bg-gray-100 text-gray-600'}`}>
                                      {at.category}
                                    </span>
                                  </span>
                                  <input
                                    type="number"
                                    min={0}
                                    step={1}
                                    value={ov?.quantity ?? ''}
                                    placeholder={String(req.quantity)}
                                    onChange={(e) => {
                                      const val = e.target.value === '' ? undefined : Math.max(0, Math.round(Number(e.target.value)));
                                      setFormData((prev) => {
                                        const reqs = prev.resourceRequirements.map((r, i) => {
                                          if (i !== idx) return r;
                                          const overrides = r.overrides.filter((o) => o.aircraftTypeId !== atId);
                                          if (val !== undefined && val !== r.quantity) {
                                            overrides.push({ aircraftTypeId: atId, quantity: val });
                                          }
                                          return { ...r, overrides };
                                        });
                                        return { ...prev, resourceRequirements: reqs };
                                      });
                                    }}
                                    className="input-field w-16 text-xs"
                                  />
                                  {ov ? (
                                    <span className="text-[10px] font-medium text-aviation-600">overridden</span>
                                  ) : (
                                    <span className="text-[10px] text-gray-400">default ({req.quantity})</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add new requirement inline form */}
              <div className="flex flex-wrap items-end gap-3 rounded-lg border border-dashed border-gray-300 bg-gray-50/50 p-3">
                <div>
                  <label className="mb-1 block text-xs text-gray-500">Type</label>
                  <select
                    value={newReq.resourceType}
                    onChange={(e) => setNewReq((p) => ({
                      ...p,
                      resourceType: e.target.value as 'HUMAN' | 'MATERIAL',
                      qualificationId: undefined,
                      materialTypeId: undefined,
                    }))}
                    className="input-field text-sm"
                  >
                    <option value="HUMAN">Human</option>
                    <option value="MATERIAL">Material</option>
                  </select>
                </div>

                <div className="min-w-[180px] flex-1">
                  <label className="mb-1 block text-xs text-gray-500">
                    {newReq.resourceType === 'HUMAN' ? 'Qualification' : 'Material Type'}
                  </label>
                  {newReq.resourceType === 'HUMAN' ? (
                    <select
                      value={newReq.qualificationId ?? ''}
                      onChange={(e) => setNewReq((p) => ({ ...p, qualificationId: e.target.value || undefined }))}
                      className="input-field text-sm"
                    >
                      <option value="">Select qualification...</option>
                      {qualifications.map((q) => (
                        <option key={q.id} value={q.id}>{q.name} ({q.code})</option>
                      ))}
                    </select>
                  ) : (
                    <select
                      value={newReq.materialTypeId ?? ''}
                      onChange={(e) => setNewReq((p) => ({ ...p, materialTypeId: e.target.value || undefined }))}
                      className="input-field text-sm"
                    >
                      <option value="">Select material type...</option>
                      {materialTypes.map((m) => (
                        <option key={m.id} value={m.id}>{m.name} ({m.code})</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="w-20">
                  <label className="mb-1 block text-xs text-gray-500">Qty</label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={newReq.quantity}
                    onChange={(e) => setNewReq((p) => ({ ...p, quantity: Math.max(1, Math.round(Number(e.target.value))) }))}
                    className="input-field text-sm"
                  />
                </div>

                <label className="flex items-center gap-1.5 pb-2 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    checked={newReq.isOptional}
                    onChange={(e) => setNewReq((p) => ({ ...p, isOptional: e.target.checked }))}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-aviation-600"
                  />
                  Optional
                </label>

                <button
                  type="button"
                  onClick={addResourceRequirement}
                  disabled={
                    (newReq.resourceType === 'HUMAN' && !newReq.qualificationId) ||
                    (newReq.resourceType === 'MATERIAL' && !newReq.materialTypeId)
                  }
                  className="btn-secondary text-sm"
                >
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  Add
                </button>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                className="input-field"
                rows={2}
                placeholder="Describe the task..."
              />
            </div>

            {/* Options */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.isRoutine}
                  onChange={(e) => setFormData((p) => ({ ...p, isRoutine: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-aviation-600 focus:ring-aviation-500"
                />
                Auto-apply (routine)
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData((p) => ({ ...p, isActive: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-aviation-600 focus:ring-aviation-500"
                />
                Active
              </label>
            </div>
          </div>

          {/* Sticky Save / Cancel buttons */}
          <div className="mt-4 flex justify-end gap-2 border-t border-gray-200 pt-4">
            <button onClick={closeForm} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
              className="btn-primary"
            >
              {(createMutation.isPending || updateMutation.isPending) ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {editingId ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {/* ── Template list ── */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-aviation-200 border-t-aviation-600" />
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTemplates.map((template) => {
            const isExpanded = expandedId === template.id;

            return (
              <div key={template.id} className="card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`rounded-lg p-2 ${template.isActive ? 'bg-aviation-50' : 'bg-gray-100'}`}>
                      <ClipboardList className={`h-5 w-5 ${template.isActive ? 'text-aviation-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{template.name}</h3>
                        <span className="badge bg-gray-100 font-mono text-xs text-gray-600">{template.code}</span>
                        {!template.isActive && (
                          <span className="badge bg-gray-100 text-gray-500">Inactive</span>
                        )}
                        {template.isRoutine && (
                          <span className="badge bg-green-50 text-xs text-green-700">
                            <Zap className="mr-0.5 inline h-3 w-3" />
                            Auto
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-sm text-gray-500">{template.description}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="hidden items-center gap-3 text-sm text-gray-500 md:flex">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {template.defaultDuration}m
                      </span>
                      {template.priority > 0 && (
                        <span className="flex items-center gap-1">
                          <BarChart3 className="h-3.5 w-3.5" />
                          P{template.priority}
                        </span>
                      )}
                      {template.phaseSchedules.map((ps) => (
                        <span key={ps.phase} className={`badge ${phaseColors[ps.phase] || 'bg-gray-100 text-gray-600'}`}>
                          {ps.phase}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center gap-1">
                      {hasMinRole(user?.role, Role.MANAGER) && (
                        <>
                          <button
                            onClick={() => startEdit(template)}
                            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            title="Edit"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          {deleteConfirmId === template.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => deleteMutation.mutate(template.id)}
                                className="rounded-lg bg-red-50 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-100"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(null)}
                                className="rounded-lg px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirmId(template.id)}
                              className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </>
                      )}
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : template.id)}
                        className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="mt-4 border-t border-gray-100 pt-4">
                    {/* Resource Requirements */}
                    <h4 className="mb-3 text-sm font-semibold text-gray-700">Resource Requirements</h4>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {template.resourceRequirements.map((req) => (
                        <div
                          key={req.id}
                          className="rounded-lg border border-gray-200 bg-gray-50 p-3"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`rounded-lg p-2 ${req.resourceType === 'HUMAN' ? 'bg-blue-50' : 'bg-amber-50'}`}>
                              {req.resourceType === 'HUMAN' ? (
                                <Users className="h-4 w-4 text-blue-600" />
                              ) : (
                                <Boxes className="h-4 w-4 text-amber-600" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">
                                {req.resourceType === 'HUMAN'
                                  ? req.qualification?.name ?? 'Unknown qualification'
                                  : req.materialType?.name ?? 'Unknown material type'}
                              </p>
                              <p className="text-xs text-gray-400">
                                {req.resourceType} ×{req.quantity}
                                {req.isOptional && ' · Optional'}
                              </p>
                            </div>
                          </div>
                          {req.overrides.length > 0 && (
                            <div className="mt-2 border-t border-gray-200 pt-2">
                              <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">Qty by type</p>
                              {req.overrides.map((ov) => (
                                <div key={ov.aircraftTypeId} className="flex items-center justify-between text-xs text-gray-500">
                                  <span>{ov.aircraftType.icaoCode} ({ov.aircraftType.category})</span>
                                  <span className="font-medium text-gray-700">×{ov.quantity}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                      {template.resourceRequirements.length === 0 && (
                        <p className="col-span-full text-sm text-gray-400">
                          No resource requirements defined
                        </p>
                      )}
                    </div>

                    {/* Aircraft Types */}
                    <div className="mt-4">
                      <h4 className="mb-2 text-sm font-semibold text-gray-700">Aircraft Types</h4>
                      <div className="flex flex-wrap gap-2">
                        {template.aircraftTypes.map((at) => (
                          <span key={at.id} className="badge bg-aviation-50 text-aviation-700">
                            {at.icaoCode} – {at.name}
                            <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] ${categoryColors[at.category] ?? 'bg-gray-100 text-gray-600'}`}>
                              {at.category}
                            </span>
                          </span>
                        ))}
                        {template.aircraftTypes.length === 0 && (
                          <span className="text-sm text-gray-400">None</span>
                        )}
                      </div>
                    </div>

                    {/* Duration Overrides */}
                    {template.durationOverrides.length > 0 && (
                      <div className="mt-4">
                        <h4 className="mb-2 text-sm font-semibold text-gray-700">Duration by Aircraft Type</h4>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                          {template.durationOverrides.map((ov) => (
                            <div key={ov.id} className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                              <div>
                                <span className="text-sm font-medium text-gray-900">{ov.aircraftType.icaoCode} – {ov.aircraftType.name}</span>
                                <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] ${categoryColors[ov.aircraftType.category] ?? 'bg-gray-100 text-gray-600'}`}>
                                  {ov.aircraftType.category}
                                </span>
                              </div>
                              <div className="text-right">
                                <span className="text-sm font-semibold text-aviation-700">{ov.duration}m</span>
                                <span className="ml-1 text-xs text-gray-400">(default: {template.defaultDuration}m)</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Phase Schedules */}
                    {template.phaseSchedules.length > 0 && (
                      <div className="mt-4">
                        <h4 className="mb-2 text-sm font-semibold text-gray-700">Phase Scheduling</h4>
                        <div className="space-y-1.5">
                          {template.phaseSchedules.map((ps) => (
                            <div key={ps.id} className="flex items-center gap-2 text-sm">
                              <span className={`badge text-xs ${phaseColors[ps.phase] ?? 'bg-gray-100 text-gray-600'}`}>{ps.phase}</span>
                              {ps.startTrigger === 'PHASE_START' && (
                                <span className="text-gray-600">→ Starts at phase start</span>
                              )}
                              {ps.startTrigger === 'PHASE_END' && (
                                <span className="text-gray-600">→ Ends at phase boundary</span>
                              )}
                              {ps.startTrigger === 'AFTER_TASK' && ps.predecessor && (
                                <span className="text-gray-600">
                                  → After &quot;{ps.predecessor.name}&quot; ({ps.predecessor.code})
                                  {ps.offsetMinutes !== 0 && ` ${ps.offsetMinutes > 0 ? '+' : ''}${ps.offsetMinutes}min`}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {filteredTemplates.length === 0 && (
            <div className="card py-12 text-center">
              <ClipboardList className="mx-auto h-10 w-10 text-gray-300" />
              <p className="mt-3 text-sm text-gray-400">No task templates found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
